/**
 * Pacchetto mensile direzione — the months list and the pack, as two addresses (issue 226, giro 3
 * «Dopo» point 8, `docs/adr/3002` amendment 2026-10-07). The pure half, tested on strings:
 *
 *   `/strategic/monthly-pack?country=…`            the closed months, one per row
 *   `/strategic/monthly-pack/:month?country=…`     one frozen pack, as a «Documento» (ADR 9053)
 *
 * `:month` is `YYYYMM`, the month the platform froze (`parseMonth`). The market stays the query
 * parameter it always was: it is the perimeter both pages share, and the breadcrumb carries it up.
 *
 * Before, one page did both, the month a query parameter (`?month=`). An old link carrying it is
 * moved to the pack's own address, replacing its history entry ({@link legacyPackRedirect}); every
 * other key it carried travels with it.
 */

import { useEffect } from 'react';

import { COUNTRY_PARAM, MONTH_PARAM, parseMonth } from './programme';

export const MONTHLY_PACK_PATH = '/strategic/monthly-pack';

function withCountry(path: string, country: string | null): string {
  if (country === null) return path;
  const params = new URLSearchParams();
  params.set(COUNTRY_PARAM, country);
  return `${path}?${params.toString()}`;
}

/** The closed months of a market — the breadcrumb's way back up. */
export function packMonthsHref(country: string | null): string {
  return withCountry(MONTHLY_PACK_PATH, country);
}

/** One frozen pack of a market. */
export function packHref(country: string | null, month: number): string {
  return withCountry(`${MONTHLY_PACK_PATH}/${month}`, country);
}

/** The month a pack's path names, or `null` — not the pack's path, or not a `YYYYMM`. Pure. */
export function readPackMonthPath(pathname: string): number | null {
  const path = pathname.replace(/\/+$/, '');
  if (!path.startsWith(`${MONTHLY_PACK_PATH}/`)) return null;
  const segment = decodeURIComponent(path.slice(MONTHLY_PACK_PATH.length + 1));
  return segment.includes('/') ? null : parseMonth(segment);
}

/**
 * Where an old months-list link that opened one month now lives, or `null`. A `?month=` that is not
 * a month names no pack, and the link stays on the list without it. Pure; `useLegacyPackRedirect`
 * replaces the entry with it.
 */
export function legacyPackRedirect(pathname: string, search: string): string | null {
  if (pathname.replace(/\/+$/, '') !== MONTHLY_PACK_PATH) return null;
  const params = new URLSearchParams(search);
  if (!params.has(MONTH_PARAM)) return null;
  const month = parseMonth(params.get(MONTH_PARAM));
  params.delete(MONTH_PARAM);
  const query = params.toString();
  const path = month === null ? MONTHLY_PACK_PATH : `${MONTHLY_PACK_PATH}/${month}`;
  return query === '' ? path : `${path}?${query}`;
}

/** Moves an old link that named a month to the pack's address, once, on mount — REPLACED, not pushed. */
export function useLegacyPackRedirect(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const target = legacyPackRedirect(window.location.pathname, window.location.search);
    if (target === null) return;
    window.history.replaceState({}, '', target);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);
}
